/**
 * Enhanced Query Client Configuration
 * 
 * Provides global error handling, retry logic, and performance optimizations
 */

import { QueryClient } from '@tanstack/react-query';
import { log } from '../utils/logger';
import { toast } from 'react-hot-toast'; // You may need to install this: npm install react-hot-toast

/**
 * Global error handler for React Query
 */
const handleQueryError = (error, query) => {
  const errorMessage = error?.message || 'An unexpected error occurred';
  const queryKey = query?.queryKey || 'unknown';

  log.error('🚨 React Query Error:', {
    error: errorMessage,
    queryKey,
    meta: query?.meta,
    timestamp: new Date().toISOString(),
  });

  // Don't show toast for background refetches or specific error types
  const shouldShowToast = 
    !query?.meta?.skipErrorToast && 
    !errorMessage.includes('Failed to fetch') && // Network errors
    !errorMessage.includes('AbortError'); // Cancelled requests

  if (shouldShowToast) {
    toast.error(errorMessage, {
      id: `error-${JSON.stringify(queryKey)}`, // Prevent duplicate toasts
      duration: 5000,
      position: 'top-right',
    });
  }
};

/**
 * Global mutation error handler
 */
const handleMutationError = (error, variables, context, mutation) => {
  const errorMessage = error?.message || 'Operation failed';
  
  log.error('🚨 React Query Mutation Error:', {
    error: errorMessage,
    variables,
    context,
    mutationKey: mutation?.options?.mutationKey,
    timestamp: new Date().toISOString(),
  });

  // Show toast for mutation errors (they're user-initiated)
  if (!mutation?.meta?.skipErrorToast) {
    toast.error(errorMessage, {
      duration: 6000,
      position: 'top-right',
    });
  }
};

/**
 * Retry function with exponential backoff
 */
const retryFunction = (failureCount, error) => {
  // Don't retry on 4xx errors (client errors)
  if (error?.status >= 400 && error?.status < 500) {
    return false;
  }

  // Don't retry on authentication errors
  if (error?.message?.includes('unauthorized') || error?.message?.includes('forbidden')) {
    return false;
  }

  // Retry up to 3 times with exponential backoff
  if (failureCount < 3) {
    log.info(`🔄 Retrying query (attempt ${failureCount + 1}/3) in ${Math.pow(2, failureCount) * 1000}ms`);
    return Math.pow(2, failureCount) * 1000;
  }

  return false;
};

/**
 * Create optimized Query Client
 */
export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time: How long data stays fresh
        staleTime: 5 * 60 * 1000, // 5 minutes for most data
        
        // Cache time: How long unused data stays in cache
        cacheTime: 10 * 60 * 1000, // 10 minutes
        
        // Retry configuration
        retry: retryFunction,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        
        // Refetch configuration - be more aggressive to handle stale cache
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
        
        // Refetch when data is stale and component mounts
        refetchOnMountOrArgChange: true,
        
        // Background refetch interval (disabled by default)
        refetchInterval: false,
        
        // Error handling
        onError: handleQueryError,
        
        // Network mode
        networkMode: 'online', // Only run queries when online
      },
      
      mutations: {
        // Retry mutations once on network errors
        retry: (failureCount, error) => {
          if (failureCount < 1 && (
            error?.message?.includes('network') ||
            error?.message?.includes('fetch')
          )) {
            return true;
          }
          return false;
        },
        
        // Error handling
        onError: handleMutationError,
        
        // Network mode
        networkMode: 'online',
      },
    },
  });
};

/**
 * Cache configuration presets for different data types
 */
export const cacheConfig = {
  // Fast-changing data (real-time availability, live stats)
  realtime: {
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 60 * 1000, // Refetch every minute
  },
  
  // Frequently updated data (reservations, games)
  frequent: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  },
  
  // Standard data (teams, tournaments)
  standard: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
  },
  
  // Rarely changing data (venues, user profiles)
  stable: {
    staleTime: 15 * 60 * 1000, // 15 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  },
  
  // Static data (configurations, constants)
  static: {
    staleTime: 60 * 60 * 1000, // 1 hour
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },
};

/**
 * Helper function to merge cache config with custom options
 */
export const withCacheConfig = (configType, customOptions = {}) => {
  return {
    ...cacheConfig[configType],
    ...customOptions,
  };
};

export default createQueryClient;